/**
 * Shared CSS sprite-frame helpers for atlas crops (e.g. one pinecone from
 * pinecones-v1.png). All instances reuse the same texture URL.
 */

export interface SpriteFrameRect {
  /** Pixel origin in the source texture (top-left). */
  x: number;
  y: number;
  /** Pixel size of the visible frame. */
  width: number;
  height: number;
}

/**
 * Apply a pixel frame crop via CSS background-size / background-position.
 * Element size should already match the frame's aspect.
 *
 * CSS % background-position is NOT "-x/frameW" — it maps a point on the
 * image onto a point on the box using (container − image) as the range.
 * For a frame (fx,fy,fw,fh) in an atlas (sw,sh) that fills the element:
 *   background-size: (sw/fw*100%) (sh/fh*100%)
 *   background-position: (fx/(sw-fw)*100%) (fy/(sh-fh)*100%)
 */
export function applySpriteFrameBackground(
  el: HTMLElement,
  frame: SpriteFrameRect,
  imageWidth: number,
  imageHeight: number,
): void {
  if (frame.width <= 0 || frame.height <= 0) return;
  if (!(imageWidth > 0) || !(imageHeight > 0)) return;
  el.classList.add('wp-scenery-layer-sprite-frame');
  const sizeX = (imageWidth / frame.width) * 100;
  const sizeY = (imageHeight / frame.height) * 100;
  const rangeX = imageWidth - frame.width;
  const rangeY = imageHeight - frame.height;
  const posX = rangeX > 0 ? (frame.x / rangeX) * 100 : 0;
  const posY = rangeY > 0 ? (frame.y / rangeY) * 100 : 0;
  // Beat ground-decal / prop contain / 100% stretch rules.
  el.style.setProperty('background-size', `${sizeX}% ${sizeY}%`, 'important');
  el.style.setProperty('background-position', `${posX}% ${posY}%`, 'important');
  el.style.setProperty('background-repeat', 'no-repeat', 'important');
}

export function clearSpriteFrameBackground(el: HTMLElement): void {
  el.classList.remove('wp-scenery-layer-sprite-frame');
  el.style.removeProperty('background-size');
  el.style.removeProperty('background-position');
  el.style.removeProperty('background-repeat');
}
